/**
 * Pending File Cache
 *
 * Module-level cache for File objects that need to persist across
 * component unmount/remount cycles within the same session.
 *
 * Files are stored in memory (not localStorage) since File objects
 * can't be serialized. The cache is keyed by draft ID.
 *
 * Each file has an `addedAt` timestamp for ordering with pending sub-items.
 */

export interface CachedPendingFile {
  file: File;
  addedAt: number; // Timestamp for ordering
}

// Cache structure: draftId -> CachedPendingFile[]
const pendingFilesCache = new Map<string, CachedPendingFile[]>();

/**
 * Store pending files for a draft (replaces existing)
 */
export function cachePendingFiles(draftId: string, files: CachedPendingFile[]): void {
  pendingFilesCache.set(draftId, [...files]);
}

/**
 * Retrieve pending files for a draft
 */
export function getCachedPendingFiles(draftId: string): CachedPendingFile[] {
  return pendingFilesCache.get(draftId) || [];
}

/**
 * Add files to existing cache for a draft
 */
export function addToCachedPendingFiles(draftId: string, files: File[]): void {
  const existing = pendingFilesCache.get(draftId) || [];
  const now = Date.now();
  const newEntries = files.map((file, i) => ({
    file,
    addedAt: now + i, // Slight offset to preserve order within batch
  }));
  pendingFilesCache.set(draftId, [...existing, ...newEntries]);
}

/**
 * Remove a file from cache by index
 */
export function removeFromCachedPendingFiles(draftId: string, index: number): void {
  const existing = pendingFilesCache.get(draftId) || [];
  existing.splice(index, 1);
  pendingFilesCache.set(draftId, existing);
}

/**
 * Clear pending files cache for a draft
 */
export function clearCachedPendingFiles(draftId: string): void {
  pendingFilesCache.delete(draftId);
}

/**
 * Clear all cached pending files (e.g., on logout)
 */
export function clearAllCachedPendingFiles(): void {
  pendingFilesCache.clear();
}

/**
 * Reorder cached pending files
 * @param draftId The draft ID
 * @param fromIndex Original index
 * @param toIndex New index
 */
export function reorderCachedPendingFiles(draftId: string, fromIndex: number, toIndex: number): void {
  const existing = pendingFilesCache.get(draftId) || [];
  if (fromIndex < 0 || fromIndex >= existing.length || toIndex < 0 || toIndex >= existing.length) {
    return;
  }
  const [item] = existing.splice(fromIndex, 1);
  existing.splice(toIndex, 0, item);
  pendingFilesCache.set(draftId, existing);
}

/**
 * Update the full list of cached pending files (for reordering)
 */
export function setCachedPendingFiles(draftId: string, files: CachedPendingFile[]): void {
  pendingFilesCache.set(draftId, files);
}
